import { round } from "lodash";
import { DataDumpType, useQueryResults } from "../../../../types";
import { ONE_HOUR_OF_MS } from "reports/utils/timeUtils";

const shifts = ["1st Shift", "2nd Shift", "3rd Shift"];

export type QiagenQuarterlyDataType = {
  machineName: string;
  skuNameStatic: string;
  totalUnitsProduced: number;
  expectedTargetProduced: number;
  percentYield: string;
  sumOfDuration: number;
};

export type QiagenQuarterlyAllSkuMachineDataType = {
  machineName: string;
  skuDataOrganizedByMachine: QiagenQuarterlyDataType[];
};

export type QiagenDataByMachineType = {
  finalDataByMachine: QiagenQuarterlyAllSkuMachineDataType[];
  finalAggregateDataByMachine: QiagenQuarterlyDataType[];
};
const bottleFillerMachemetryId = "qi_d_01_3_0";
const crimperMachemetryId = "qi_d_01_7_0";
const SCKMachemetryId = "qi_d_01_2_0";
const smallVolumeFillerMachemetryId = "qi_d_01_8_0";
const tubeFillerMachemetryId = "qi_d_01_1_0";
const mactecPoucherMachemetryId = "qi_d_01_5_0";
const vialFillerMachemetryId = "qi_d_01_6_0";
const qftSideBaggerMachemetryId = "qi_d_01_4_0";
const spinColumnSystemMachemetryId = "qi_d_01_9_0";
const sortimatMachemetryId = "qi_d_01_10_0";
const mb25FillerCapperMachemetryId = "qi_d_01_11_0";
const ez1SystemMachemetryId = "qi_d_01_12_0";

const defaultSkuString = "Default SKU";
const defaultSkuId = "Default";

const bottleFillerSkus = [
  "Buffer RW1 (45/60) N",
  "Buffer AE (15/15)",
  "Buffer AE (60/60)",
  "Buffer AW2(13/60)",
  "Buffer AL-T/M (12/15)",
  "Buffer AL-T/M (33/60)",
  "Buffer EB (15/15)",
  "Buffer EB (55/60)",
  "Buffer ATL (50/60)",
  "Buffer AW1 (19/60)",
  "Buffer N3 (30/30)",
  "Buffer P1 (20/30)",
  "Buffer P1 (50/60)",
  "Buffer QG (50/60)",
  "Buffer P2 (20/30)",
  "Buffer P2 (50/60)",
  "Buffer PE (10/60)",
  "Buffer PE (6/30)",
  "Buffer RLT (45/60)",
  "Buffer RPE (11/60) N",
  "RNase-free water (10/15)",
  "DEN RGT 1.75N B0352 (100ML/125ML)",
  "PRB DIL B0716 (20ML/30ML)",
  "DET RGT 1 B1001 (40ML/60ML)",
  "DET RGT 2 R0401 (40ML/60ML)",
  "DEN RGT 1.75N B0352 (50ML/60ML)",
  "PRB DIL B0716 (5ML/15ML)",
  "DET RGT 1 B1001 (12ML/15ML)",
  "DET RGT 2 R0401 (12ML/15ML)",
  "BFR SMPL CONV, B0881 (100ML/125ML)",
  "SPEC TRANS MED, B0026 (30ML/30ML)",
  "DEN RGT 1.75N B0352 (12ML/15ML)",
  "Wash Buffer Concentrate(100ML/125ML)",
  "Buffer ATL (14/15)	",
  "QFN Stop Solution (15ml/30ml)(4G-I)",
  "QFN Wash Buffer 20x (100ml/125ml)(4G-I)",
  "QFN Enzyme Subs Sol (30ml/60ml)(4G-I)",
  "QFN Green Diluent,(30ml/30ml)(4G-I)",
  "QFN Green Diluent,(30ml/30ml)(4G-I), CE",
  "QFN Stop Solution (15ml/30ml)(4G-I), CE",
  "QFNWash Buff 20x(100ml/125ml)(4G-I),CE",
  "QFN Enzyme Sub Sol(30ml/60ml)(4G-I),CE",
  defaultSkuString,
];

const crimperAndVialLabelerSkus = [
  "Crimp and Label",
  "Crimping Only",
  "Labeling Only",
  defaultSkuString,
];

const SCK = ["SCK"];

const smallVolumeFillerSkus = [
  "AmniSure",
  "Indicator Dye - 0.35mL Fill",
  "Indicator Dye - 2mL Fill",
  "ASSM-5R00008-36V",
];

const tubeFillerSkus = ["Tube Filler - STM", "Tube Filler - HC2"];

const mactecPoucherSkus = ["AmniSure Poucher"];

const vialFillerSkus = [
  "Std 1X",
  "Std 2X",
  "Conjugate",
  "Mitogen",
  "Sample Cap",
  "Ctl Panel 1,2,3",
  defaultSkuString,
];

const spinColumnSkus = [defaultSkuString];

const qftSideBaggerSkus = ["QFT-Plus Dispenser Pack"];

const sortimatSkus = [
  defaultSkuString,
  "QIAfilter 96 Plate (1) (03/06)",
  "PAXgene 96 RNA Plate (1) (03/06)",
  "PAXgene 96 Filter Plate (1) (03/06)",
  "PAXgene 96 RNA Plate (1) FLU(03/06)",
  "PAXgene 96 Filter Pl. (1) FLU(03/06)",
  "QIAprep 96 Plate (1) (03/06)",
  "QIAamp 96 Plate (1) (03/06)",
  "QIAquick 96 Plate (1) (03/06)",
  "RNeasy 96 Plate (1) (03/06)",
  "TurboFilter 96 Plate (1) (03/06)",
  "DNeasy 96 Plate (1) (03/06)",
  "AllPrep DNA 96 plate (1)",
  "EpiTect 96 Plate (1) (06/06)",
  "gDNA Eliminator 96 plate (1)",
  "Placa de Vacuo (1) BioM",
  "Plasmid plus 96 plate (1) (03/06)",
  "96 Column Well Plates (1) Tf",
];

const mb25Skus = [
  defaultSkuString,
  "Indicator Dye (2ml/3ml)",
  "Indicator Dye (0.35ml/3ml)",
  "Solution with vial and cap (0.5ml)",
  "AmniSure",
  "PartoSure",
];

const ez1Skus = ["EZ1 Cartridgein Blister"];

const bottleFillerName = "Bottle Filler";
export const machineNames = [
  bottleFillerName,
  "Crimper and Vial Labeler",
  "SCK",
  "Small Volume Filler",
  "Tube Filler",
  "Mactec Poucher",
  "Vial Filler",
  "QFT Side Bagger",
  "Spin Column System",
  "Sortimat",
  "MB25",
  "EZ1",
];

export const machineNamesAndSkus = [
  {
    machineName: bottleFillerName,
    staticMachemetryId: bottleFillerMachemetryId,
    skus: bottleFillerSkus,
  },
  {
    machineName: "Crimper and Vial Labeler",
    staticMachemetryId: crimperMachemetryId,
    skus: crimperAndVialLabelerSkus,
  },
  { machineName: "SCK", staticMachemetryId: SCKMachemetryId, skus: SCK },
  {
    machineName: "Small Volume Filler",
    staticMachemetryId: smallVolumeFillerMachemetryId,
    skus: smallVolumeFillerSkus,
  },
  {
    machineName: "Tube Filler",
    staticMachemetryId: tubeFillerMachemetryId,
    skus: tubeFillerSkus,
  },
  {
    machineName: "Mactec Poucher",
    staticMachemetryId: mactecPoucherMachemetryId,
    skus: mactecPoucherSkus,
  },
  {
    machineName: "Vial Filler",
    staticMachemetryId: vialFillerMachemetryId,
    skus: vialFillerSkus,
  },
  {
    machineName: "QFT Side Bagger",
    staticMachemetryId: qftSideBaggerMachemetryId,
    skus: qftSideBaggerSkus,
  },
  {
    machineName: "Spin Column System",
    staticMachemetryId: spinColumnSystemMachemetryId,
    skus: spinColumnSkus,
  },
  {
    machineName: "Sortimat",
    staticMachemetryId: sortimatMachemetryId,
    skus: sortimatSkus,
  },
  {
    machineName: "MB25",
    staticMachemetryId: mb25FillerCapperMachemetryId,
    skus: mb25Skus,
  },
  {
    machineName: "EZ1",
    staticMachemetryId: ez1SystemMachemetryId,
    skus: ez1Skus,
  },
];

// the skus are organized on their own in order to format the indexes for the graph with each sku needs its own index
// and the major axis being determined by machine
export const qiagenSkus = machineNamesAndSkus.flatMap(
  ({ machineName, skus, staticMachemetryId }) =>
    skus.map(skuNameStatic =>
      skuNameStatic !== defaultSkuString
        ? {
            staticMachemetryId,
            skuNameStatic,
          }
        : {
            // default needs to add the defaultSkuId of static
            staticMachemetryId,
            skuNameStatic,
            skuIdStatic: defaultSkuId,
          }
    )
);

/**
 *
 * @param percentage A String value representing a percentage i.e. 10.23%
 * @return a number representing the percentage i.e. 10.23% -> .1023
 */
const convertPercentageStringToNumber = (percentage: string): number => {
  const numberComponentOfString = percentage.split("%")[0];

  return Number(numberComponentOfString) / 100;
};

export const sortByYield = (
  a: QiagenQuarterlyDataType,
  b: QiagenQuarterlyDataType
) => {
  const keyA = convertPercentageStringToNumber(a.percentYield);
  const keyB = convertPercentageStringToNumber(b.percentYield);

  return keyB - keyA;
};

export const getQuarterTimeSpan = (
  year,
  quarter
): { quarterGe: Date; quarterLt: Date } => {
  let startMonth = 1;
  let endYear = year;
  let endMonth = 4;
  switch (quarter) {
    case "Q1":
      break;
    case "Q2":
      startMonth = 4;
      endMonth = 7;
      break;
    case "Q3":
      startMonth = 7;
      endMonth = 10;
      break;
    case "Q4":
      startMonth = 10;
      endMonth = 1;
      endYear++;
      break;
    default:
  }
  const quarterGe = new Date(`${year}-${startMonth}-00:00:00`);
  const quarterLt = new Date(`${endYear}-${endMonth}-00:00:00`);

  return { quarterGe, quarterLt };
};

const QUERY_DIVIDING_UNIT = 12;
/**
 * Divide our given timespan (expected to be a quarter) into 6 even parts
 *
 * @param ge start of timespan
 * @param lt end of timespan
 */
export const splitUpTimespan = (startDate: Date, endDate: Date) => {
  const totalDuration = endDate.getTime() - startDate.getTime();

  const dividingUnit = totalDuration / QUERY_DIVIDING_UNIT; // we split in bi-weekly chunks so we don't exceed our query timespan

  const startTimeArray = [...Array(QUERY_DIVIDING_UNIT)].map((entry, index) => {
    return {
      ge: startDate.getTime() + dividingUnit * index, // we generate our Ge's first
      lt: 0,
    };
  });

  return startTimeArray.map(({ ge, lt }) => {
    return {
      ge: ge,
      lt: ge + dividingUnit,
    };
  });
};

/**
 * Check to see if all our data successfully loaded in the array
 *
 * @param dataDumpArray The array which will contain, {loading, data, error}
 */
export const allDataLoaded = (dataDumpArray: useQueryResults[]) => {
  const arrayOfDataStillLoading = dataDumpArray.filter(entry => entry.loading);

  return arrayOfDataStillLoading.length === 0;
};

export const dataDumpGetterQI = (dataDumpsArray: DataDumpType[]) => {
  const dataDumpTotalCountAggregate = qiagenSkus.map(
    ({ staticMachemetryId, skuNameStatic, skuIdStatic }) => {
      const totalUnitsProduced = dataDumpsArray?.reduce(
        (productionCount, { skuName, netOutput, machemetryId, sku }) => {
          return (skuNameStatic === skuName || skuIdStatic === sku) &&
            staticMachemetryId === machemetryId
            ? productionCount + netOutput
            : productionCount;
        },
        0
      );

      const expectedTargetProduct = dataDumpsArray?.reduce(
        (
          expectedCount,
          { targetRate, ge, lt, skuScale, skuName, machemetryId, sku }
        ) => {
          const adjustedTargetRate = targetRate || 0;
          const adjustedSkuScale = skuScale || 0;

          const netExpectedCount =
            ((parseInt(lt, 10) - parseInt(ge, 10)) / 60000) *
            adjustedTargetRate *
            adjustedSkuScale;

          return (skuNameStatic === skuName || skuIdStatic === sku) &&
            staticMachemetryId === machemetryId
            ? expectedCount + netExpectedCount
            : expectedCount;
        },
        0
      );

      const sumOfDuration = round(
        dataDumpsArray?.reduce(
          (totalDuration, { ge, lt, skuName, machemetryId, sku }) => {
            return (skuNameStatic === skuName || skuIdStatic === sku) &&
              staticMachemetryId === machemetryId
              ? totalDuration + (Number(lt) - Number(ge)) / ONE_HOUR_OF_MS
              : totalDuration;
          },
          0
        ),
        1
      );

      let percentYield = (
        (totalUnitsProduced / expectedTargetProduct) *
        100
      ).toFixed(1);
      percentYield += "%";
      const expectedTargetProduced = Math.ceil(expectedTargetProduct);

      // find the name of the machine based on tag
      const machineDetails = machineNamesAndSkus.find(
        ({ staticMachemetryId: staticMachemetryIdMachineDetail }) => {
          return staticMachemetryIdMachineDetail === staticMachemetryId;
        }
      );

      const { machineName } = machineDetails
        ? machineDetails
        : { machineName: "" };

      return {
        machineName,
        skuNameStatic,
        totalUnitsProduced,
        expectedTargetProduced,
        percentYield,
        sumOfDuration,
      } as QiagenQuarterlyDataType;
    }
  );

  // we need to create two data set
  // the first will be our sku piled up by machineName, indexing based on MachineName
  // the next will be the all the data formatted and filtered in a single array for use for the chart, with the indexing based on Sku's

  const finalDataByMachine: QiagenQuarterlyAllSkuMachineDataType[] =
    machineNames.map(machineName => {
      // gather all data related to a machine
      const initialMachineGrouping = dataDumpTotalCountAggregate.filter(
        dataPoint => dataPoint.machineName === machineName
      );

      // sort by yield, and filter out skus that are not expected to produce anything
      const skuDataOrganizedByMachine = initialMachineGrouping
        .filter(dataPoint => dataPoint.expectedTargetProduced !== 0)
        .sort(sortByYield);

      return {
        machineName,
        skuDataOrganizedByMachine,
      } as QiagenQuarterlyAllSkuMachineDataType;
    });

  // we need to add a sum of all skus data for a machine to act as a *Major* row for a machine
  const finalAggregateDataByMachine = finalDataByMachine.map(
    ({ machineName, skuDataOrganizedByMachine }) =>
      // we summate all our sku data to create the overall data (aggregate) for the machine
      // this will be the first row in a machine grouping in an overview
      sumQiagenQuarterlyData(skuDataOrganizedByMachine, machineName)
  );

  // find the top 5 bottle filler data
  const bottleFillerTopFive = dataDumpTotalCountAggregate
    // first find what is part of the bottle fillers
    .filter(
      ({ machineName, expectedTargetProduced }) =>
        machineName === bottleFillerName && expectedTargetProduced !== 0
    )
    .sort(sortByYield) // then we sort them from highest yield to lowest
    .slice(0, 5); // finally, we take out the top 5

  // split up the bottle filler data from the non bottle filler
  const nonBottleFillerData = dataDumpTotalCountAggregate.filter(
    ({ skuNameStatic, machineName }) =>
      machineName !== bottleFillerName ||
      (skuNameStatic === defaultSkuString && machineName !== bottleFillerName)
  );

  const finalDataBySku = bottleFillerTopFive
    .concat(nonBottleFillerData) // re-add the bottle filler data
    .filter(
      qiagenQuarterlyData => qiagenQuarterlyData.expectedTargetProduced !== 0 // remove all empty values, they are unnecessary
    );

  // we need to get a count of how many times each machine is used to generate ticks for the Graph
  const machineCounts = machineNames.map(singleMachineName => ({
    machineName: singleMachineName,
    count: finalDataBySku.filter(
      ({ machineName }) => machineName === singleMachineName
    ).length,
  }));

  // generate the ticks based on the number of skus which correspond to a particular machine (greater axis)
  const generatedMachineTicks = machineCounts
    .map(({ machineName, count }) => {
      const isEven = count % 2 === 0;
      return [...new Array(count)].map((obj, index, originalArray) => {
        let tick = machineName;
        const middleIndex = isEven ? count / 2 - 1 : Math.floor(count / 2);

        if (index === 0) {
          tick = tick.concat(":start"); // we generate a topline on the first index
        }
        if (index === middleIndex) {
          tick = tick.concat(":middle"); // we generate the actual machineName in the middle
        }
        if (index === originalArray.length - 1) {
          tick = tick.concat(":end"); // generate bottomline at the last index
        }
        tick = tick.concat(`:${count}`);
        return tick;
      });
    })
    .flat();

  return {
    dataBySku: finalDataBySku.map(
      (finalDatum, index) =>
        ({
          ...finalDatum,
          machineName: generatedMachineTicks[index], // generatedMachineTicks indexing should match with finalDataBySku
        }) as QiagenQuarterlyDataType
    ),
    dataByMachine: {
      finalDataByMachine,
      finalAggregateDataByMachine,
    } as QiagenDataByMachineType,
  };
};

export const separateByShift = (
  dataDumpsArray: DataDumpType[]
): {
  shiftOne: DataDumpType[];
  shiftTwo: DataDumpType[];
  shiftThree: DataDumpType[];
} => {
  const [shiftOne, shiftTwo, shiftThree]: DataDumpType[][] = shifts.map(
    shiftName => dataDumpsArray.filter(({ shift }) => shift === shiftName)
  );

  return {
    shiftOne,
    shiftTwo,
    shiftThree,
  };
};

export const sumQiagenQuarterlyData = (
  QiagenQuarterlyDataSet: QiagenQuarterlyDataType[],
  replacementName: string
) =>
  QiagenQuarterlyDataSet.reduce(sumQiagenQuarterlyDataReducer, {
    machineName: replacementName,
    skuNameStatic: replacementName,
    totalUnitsProduced: 0,
    expectedTargetProduced: 0,
    percentYield: "0%",
    sumOfDuration: 0,
  } as QiagenQuarterlyDataType);

const sumQiagenQuarterlyDataReducer = (
  aggregateData: QiagenQuarterlyDataType,
  QiagenQuarterlyData: QiagenQuarterlyDataType
) => {
  // these values can be summed

  // units produced and expected are integer counts and can be added
  aggregateData.totalUnitsProduced += QiagenQuarterlyData.totalUnitsProduced;

  aggregateData.expectedTargetProduced +=
    QiagenQuarterlyData.expectedTargetProduced;

  // sum of duration is a fraction so we need to round on the last machine
  aggregateData.sumOfDuration += QiagenQuarterlyData.sumOfDuration;

  // This is a percentage value and needs to be calculated (rounded to 1 decimal)
  aggregateData.percentYield = `${round(
    (aggregateData.totalUnitsProduced / aggregateData.expectedTargetProduced) *
      100,
    1
  )}%`;

  return aggregateData; // return aggregate when calculations are complete
};
